import { useState, useEffect, useCallback, useRef, useMemo, useContext } from 'react';
import { useTheme } from '../context/theme';
import { Ctx } from '../context/auth';
import { api } from '../utils/api';
import { useIsMobile, useAutoRefresh } from '../utils/hooks';
import { renderIcon } from '../utils/icons';
import { Btn, Inp, Avatar } from './ui';

export function Chat({ open, onClose, sites }) {
  const { theme, isDark } = useTheme();
  const { user } = useContext(Ctx);
  const [msgs, sM] = useState([]);
  const [txt, sT] = useState('');
  const [ch, sCh] = useState('general');
  const [channels, sChs] = useState([]);
  const [sites2, sS2] = useState([]);
  const endRef = useRef(null);
  const mobile = useIsMobile();

  useEffect(() => {
    api.get('/api/chat/channels').then(d => sChs(d.channels || [])).catch(() => {});
    api.get('/api/sites').then(d => sS2(d.sites || [])).catch(() => {});
  }, []);

  const allChs = useMemo(() => {
    const base = [{ slug: 'general', name: 'General', icon: 'message-circle' }];
    sites2.forEach(s => {
      if (!base.find(c => c.slug === 'site-' + s.id)) base.push({ slug: 'site-' + s.id, name: s.name, icon: 'globe' });
    });
    channels.forEach(c => {
      if (!base.find(b => b.slug === c.slug)) base.push({ slug: c.slug, name: c.name, icon: 'megaphone' });
    });
    return base;
  }, [sites2, channels]);

  const load = useCallback(async () => {
    try { const d = await api.get('/api/chat/' + ch); sM(d.messages || []); return d; } catch {}
  }, [ch]);

  useAutoRefresh(load, 10000, { enabled: open, key: 'chat' });

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs]);

  const send = async () => {
    if (!txt.trim()) return;
    try { await api.post('/api/chat/' + ch, { message: txt }); sT(''); load(); } catch (e) { alert(e.message); }
  };

  if (!open) return null;
  const sbW = mobile ? 48 : 140;

  return (
    <div style={{ position: 'fixed', right: 0, top: 0, bottom: 0, width: 'min(420px,100vw)', background: theme.bgCard, borderLeft: `1px solid ${theme.border}`, zIndex: 100, display: 'flex', flexDirection: 'row', animation: 'fadeIn .2s ease' }}>
      <div style={{ width: sbW, minWidth: sbW, borderRight: `1px solid ${theme.border}`, overflowY: 'auto', paddingTop: 8 }}>
        {allChs.map(c => (
          <button key={c.slug} className="hover-bg" onClick={() => sCh(c.slug)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: mobile ? '10px 0' : '8px 12px', justifyContent: mobile ? 'center' : 'flex-start', cursor: 'pointer', border: 'none', background: c.slug === ch ? 'rgba(255,255,255,0.10)' : 'transparent', color: c.slug === ch ? '#fff' : theme.textSecondary, fontSize: 13, fontWeight: c.slug === ch ? 600 : 400, width: '100%', borderRadius: 4 }}>
            <span>{renderIcon(c.icon, 14)}</span>
            {!mobile && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{c.name}</span>}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 400, fontFamily: "'DM Sans', sans-serif", letterSpacing: '-0.01em', color: theme.text }}>#{(allChs.find(c => c.slug === ch) || {}).name || ch}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: theme.textSecondary, cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 14 }}>
          {msgs.length === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.8 }}>{renderIcon('message-circle', 40)}</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: theme.text, marginBottom: 6 }}>No messages yet</div>
              <div style={{ fontSize: 13, color: theme.textSecondary }}>Start a conversation with your team</div>
            </div>
          )}
          {msgs.map(m => {
            const mine = m.user_id === user.id;
            return (
              <div key={m.id} style={{ display: 'flex', gap: 8, marginBottom: 10, flexDirection: mine ? 'row-reverse' : 'row', alignItems: 'flex-start' }}>
                <Avatar name={m.author} color={m.avatar_color} sz={28} />
                <div style={{ maxWidth: '75%' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 2 }}>
                    <span style={{ fontSize: 13, color: m.avatar_color || theme.text, fontWeight: 600 }}>{m.author}</span>
                    <span style={{ fontSize: 11, color: theme.textSecondary }}>{new Date(m.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
                  </div>
                  <div style={{ padding: '8px 12px', borderRadius: mine ? '12px 4px 12px 12px' : '4px 12px 12px 12px', background: mine ? 'rgba(0,0,0,0.15)' : theme.bgHover, fontSize: 14, color: theme.text, lineHeight: 1.5 }}>
                    {m.message}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        <div style={{ padding: 10, borderTop: `1px solid ${theme.border}`, display: 'flex', gap: 8 }}>
          <Inp value={txt} onChange={e => sT(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()} placeholder="Type a message..." style={{ flex: 1, padding: '10px 14px' }} />
          <Btn primary sm onClick={send}>Send</Btn>
        </div>
      </div>
    </div>
  );
}
